import { patchState, signalStore, type, withComputed, withMethods, withProps, withState } from "@ngrx/signals"
import { ClinicService } from "../../services/clinic/clinic.service"
import { computed, inject } from "@angular/core"
import { UserFacade } from "../user/user.facade"
import { exhaustMap, firstValueFrom, map, pipe, tap } from "rxjs"
import { addEntity, setAllEntities, setEntities, updateEntity, withEntities } from "@ngrx/signals/entities"
import { extractErrorMessage } from "../../interceptors/helpers/extract-error-message.helper"
import { formatProviderApiError } from "../../interceptors/helpers/provider-conflict-error.helper"
import { rxMethod } from "@ngrx/signals/rxjs-interop"
import { tapResponse } from "@ngrx/operators"
import { mapClinicDemographicsApiToState } from "../../models/clinic/clinic-demographics.model"
interface ClinicState {
    loading: boolean
    id: number
    clinic_name: string
    clinic_website: string
    npi: string
    provider_npi: string
    active: boolean
    previous_plan: string | null
    previous_discount: boolean | number | string | null
    previous_discount_name: string | null
    clinicStatus: string
    created_at: string
    error: {
        error: boolean, message: string
    }
}

interface ClinicLocation {
    id: number;
    clinic_id: number;
    address: string;
    address2: string;
    city: string;
    state: string;
    zip_code: string;
    phone: string;
    email_address: string;
    active: number
}

interface ClinicAdmin {
    id: number
    clinic_id: number
    first_name: string
    last_name: string
    email_address: string
    phone: string
    active: boolean,
    end_date: string
}

export const ClinicStore = signalStore(
    {providedIn: 'root'},
    withState<ClinicState>({
        loading: false,
        id: 0,
        clinic_name: '',
        clinic_website: '',
        npi: '',
        provider_npi: '',
        previous_plan: null,
        previous_discount: null,
        previous_discount_name: null,
        active: false,
        clinicStatus: 'idle',
        created_at: '',
        error: {
            error: false, message: ''
        },
    }),
    withEntities({
        entity: type<ClinicLocation>(), collection: 'clinic_location'
    }),
    withEntities({
        entity: type<ClinicAdmin>(), collection: 'clinic_admin'
    }),
    withComputed((store) => ({
        demographics: computed(() => ({
            id: store.id(),
            clinic_name: store.clinic_name(),
            clinic_website: store.clinic_website(),
            npi: store.npi(),
            provider_npi: store.provider_npi(),
            active: store.active(),
            previous_plan: store.previous_plan(),
            previous_discount: store.previous_discount(),
            previous_discount_name: store.previous_discount_name(),
            created_at: store.created_at()
        })),
        clinic_id: computed(() => store.id()),
        locations: computed(() => Object.values(store.clinic_locationEntities())),
        admins: computed(() => Object.values(store.clinic_adminEntities()))
    })),
    withProps((store) => ({
        clinicService: inject(ClinicService),
        userFacade: inject(UserFacade)
    })),
    withMethods((store) => {
        const service = inject(ClinicService)
        const facade = inject(UserFacade)

        return {
            clear() {
                patchState(store, {
                    loading: false,
                    id: 0,
                    clinic_name: '',
                    clinic_website: '',
                    npi: '',
                    provider_npi: '',
                    active: false,
                    previous_plan: null,
                    previous_discount: null,
                    previous_discount_name: null,
                    error: {
                        error: false, message: ''
                    },
                })
            },

            async setOnboardingStatus(clinic_id: number, provider_id: number, onboarding_status: number, callback?: () => void) {
                patchState(store, {loading: true})

                try {
                    const status = await firstValueFrom(
                        service.setOnboardingStatus(clinic_id, provider_id, onboarding_status).pipe(
                            map((response: any) => response)
                        )
                    )

                    facade.setOnboardingStatus(status.onboarding_status)

                    if(callback) callback()
                } catch(err) {
                    patchState(store, {
                        error: {
                            error: true,
                            message: extractErrorMessage(err)
                        }
                    })
                } finally {
                    patchState(store, { loading: false})
                }
            },

            async addAdmin(clinic_id: number, dto: any, callback?: () => void) {
                this.clearError()
                this.setLoading(true)

                try {
                    const saveAdmin: any = await firstValueFrom(
                        service.saveClinicAdmin(clinic_id, dto).pipe(
                            map((response: any) => response)
                        )
                    )

                    const id = saveAdmin.clinic_admin_id
                    const _dto = {...dto, id, clinic_id, active: 0}

                    patchState(store, addEntity(_dto, { collection: 'clinic_admin'}))

                    callback?.()
                } catch(err) {
                    this.setError(true, extractErrorMessage(err))
                } finally {
                    this.setLoading(false)
                }
            },

            async deleteAdmin(clinic_id: number, admin: any, callback?: () => void) {
                this.clearError()
                this.setLoading(true)

                try {
                    const update = await firstValueFrom(
                        service.deleteClinicAdmin(clinic_id, admin.id).pipe(
                            map((response: any) => response)
                        )
                    )

                    patchState(store,
                        updateEntity({
                            id: admin.id,
                            changes: {
                                active: update.active
                            }
                        }, { collection: 'clinic_admin'})
                    )

                    callback?.()
                } catch(err) {
                    this.setError(true, extractErrorMessage(err))
                } finally {
                    this.setLoading(false)
                }
            },

            setLocation(location: ClinicLocation) {
                patchState(store, addEntity(location, {collection: 'clinic_location'}))
            },

            updateExistingLocation(location: ClinicLocation) {
                patchState(store, updateEntity({
                    id: location.id,
                    changes: location,
                }, {collection: 'clinic_location'}));
            },

            setAllLocations(locations: any[]) {
                patchState(store, setAllEntities(locations, { collection: 'clinic_location'}))
            },

            async addLocation(newLocation: ClinicLocation, callback: () => void) {
                this.clearError()
                this.setLoading(true)

                try {
                    const location = await firstValueFrom(
                        service.saveLocation(newLocation).pipe(
                            map((response:any) => response)
                        )
                    )

                    patchState(store, addEntity(location, { collection: 'clinic_location'}))
                    callback()
                } catch(err) {
                    this.setError(true, extractErrorMessage(err))
                } finally {
                    this.setLoading(false)
                }
            },

            async updateLocation(location: ClinicLocation, callback: () => void) {
                this.clearError()
                this.setLoading(true)

                try {
                    await firstValueFrom(
                        service.saveLocation(location)
                    )

                    patchState(store,
                        updateEntity({
                            id: location.id,
                            changes: location
                        }, { collection: 'clinic_location'})
                    )

                    callback()
                } catch(err) {
                    this.setError(true, extractErrorMessage(err))
                } finally {
                    this.setLoading(false)
                }
            },

            async deleteLocation(location: ClinicLocation, callback: () => void) {
                this.clearError()
                this.setLoading(true)

                try {
                    const update = await firstValueFrom(
                        service.deleteLocation(location).pipe(
                            map((response: any) => response)
                        )
                    )

                    patchState(store,
                        updateEntity({
                            id: location.id,
                            changes: {
                                active: update.active
                            }
                        }, { collection: 'clinic_location'})
                    )

                    callback()
                } catch(err) {
                    this.setError(true, extractErrorMessage(err))
                } finally {
                    this.setLoading(false)
                }
            },

            clearError() {
                patchState(store, {
                    error: {
                        error: false,
                        message: ''
                    }
                })
            },

            setError(error: boolean, message: string) {
                patchState(store, {
                    error: {
                        error,
                        message
                    }
                })
            },

            setLoading(loading: boolean) {
                patchState(store, { loading })
            },

            async getDemographics(clinic_id = 0, provider_id = 0) {
                this.clearError()

                if(store.id() > 0) return

                this.setLoading(true)

                try {
                    const demographics: any = await firstValueFrom(
                        service.getClinicDemoGraphics(facade.clinic_id(), facade.provider_id()).pipe(
                            map((response: any) => response)
                        )
                    )

                    if(demographics) {
                       patchState(store, mapClinicDemographicsApiToState(demographics))

                        const locations: ClinicLocation[] = demographics.locations ?? []
                        patchState(store, setEntities(locations, { collection: 'clinic_location'}))

                        const admins: ClinicAdmin[] = demographics.admins ?? []
                        patchState(store, setEntities(admins, { collection: 'clinic_admin'}))
                    }
                } catch(err) {
                    this.setError(true, extractErrorMessage(err))
                } finally {
                    this.setLoading(false)
                }
            },

            async getClinicAdmins() {
                this.clearError()

                const check = Object.values(store.clinic_adminEntities()).length
                if(check > 0) return

                this.setLoading(true)

                try {
                    const admins: ClinicAdmin[] = await firstValueFrom(
                        service.getClinicAdmins(facade.clinic_id()).pipe(
                            map((response: any) => response.admins)
                        )
                    )

                    patchState(store, setEntities(admins, {collection: 'clinic_admin'}))
                } catch(err) {
                    this.setError(true, extractErrorMessage(err))
                } finally {
                    this.setLoading(false)
                }
            },

            setClinicStatus(clinicStatus: string) {
                patchState(store, {
                    clinicStatus
                })
            }
        }
    }),
    withMethods((store) => ({
        getClinicDemographics: rxMethod<{
            clinic_id?: number,
            provider_id?: number,
            onSuccess?: () => void
        }>(pipe(
            tap(() => {
                store.clearError();
                store.setLoading(true);
            }),
            map((params) => ({
                ...params,
                clinic_id: params.clinic_id ?? store.userFacade.clinic_id(),
                provider_id: params.provider_id ?? store.userFacade.provider_id()
            })),
            exhaustMap(({clinic_id, provider_id, onSuccess}) =>
                store.clinicService.getClinicDemoGraphics(clinic_id, provider_id).pipe(
                    tapResponse({
                        next: (demographics: any) => {
                            if(demographics) {
                            patchState(store, mapClinicDemographicsApiToState(demographics))

                                const locations: ClinicLocation[] = demographics.locations ?? []
                                patchState(store, setEntities(locations, { collection: 'clinic_location'}))

                                const admins: ClinicAdmin[] = demographics.admins ?? []
                                patchState(store, setEntities(admins, { collection: 'clinic_admin'}))

                                onSuccess?.()
                            }
                        },
                        error: (err: unknown) => {
                            store.setError(true, extractErrorMessage(err))
                        },
                        finalize: () => {
                            store.setLoading(false)
                        }
                    })
                )
            )
        )),

        activateClinic: rxMethod<{clinic_id: number, onSuccess?: () => void}>(
            pipe(
                tap(() => {
                    store.clearError()
                    store.setClinicStatus('loading')
                }),
                exhaustMap(({clinic_id, onSuccess}) =>
                    store.clinicService.activateClinic(clinic_id).pipe(
                        tapResponse({
                            next: (response: any) => {
                                patchState(store, {
                                    active: response.active
                                })

                                store.setClinicStatus('success')
                                onSuccess?.()
                            },
                            error: (err: unknown) => {
                                store.setError(true, extractErrorMessage(err))
                                store.setClinicStatus('error')
                            },
                        })
                    )
                )
            )
        ),

        saveDemographics: rxMethod<{dto: any, onSuccess?: () => void}>(
            pipe(
                tap(() => {
                    store.clearError()
                    store.setClinicStatus('loading')
                }),
                exhaustMap(({dto, onSuccess}) =>
                    store.clinicService.updateClinicDemographics(dto.clinic_id, dto.demographics).pipe(
                        tapResponse({
                            next: () => {
                                patchState(store, 
                                mapClinicDemographicsApiToState(dto.demographics))

                                store.setClinicStatus('success')
                                onSuccess?.()
                            },
                            error: (err: unknown) => {
                                store.setError(true, formatProviderApiError(err, {
                                    npi: dto.demographics.individual_npi ?? dto.demographics.clinic_npi
                                }))
                                store.setClinicStatus('error')
                            }
                        })
                    )
                )
            )
        ),

        deactivateClinic: rxMethod<{clinic_id: number, onSuccess?: () => void}>(
            pipe(
                tap(() => {
                    store.clearError()
                    store.setClinicStatus('loading')
                }),
                exhaustMap(({clinic_id, onSuccess}) =>
                    store.clinicService.deactivateClinic(clinic_id).pipe(
                        tapResponse({
                            next: () => {
                                patchState(store, {
                                    active: false
                                })

                                store.setClinicStatus('success')
                                onSuccess?.()
                            },
                            error: (err: unknown) => {
                                store.setError(true, extractErrorMessage(err))
                                store.setClinicStatus('error')
                            }
                        })
                    )
                )
            )
        )
    }))
)
